import { WHAT_TO_BRING } from "@/data/content";
import { Check, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * preview=true (Home): compact cards with items tucked inside a native
 * <details> "Learn more" toggle to reduce visual density. A link points to the
 * full checklist on the dedicated trip page.
 * preview=false: full checklist (used on the October 2026 page).
 */
export default function WhatToBring({ preview = false }) {
  return (
    <section data-testid="whattobring-section" className="py-20 md:py-28 bg-[var(--maqam-surface)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <span className="overline">Prepare With Confidence</span>
          <h2 className="font-serif text-4xl md:text-5xl text-navy mt-3 leading-[1.05] tracking-tight">
            What to bring for Umrah
          </h2>
          <p className="mt-5 text-[var(--maqam-muted)] leading-relaxed">
            A simple, practical checklist used by our community of travellers.
            {preview ? " Tap any category to see the essentials." : " Save it, print it, share it."}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {WHAT_TO_BRING.map((group, i) => {
            const ItemsList = (
              <>
                <ul className="mt-5 space-y-3">
                  {group.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-[var(--maqam-ink)]">
                      <span className="mt-1 inline-flex w-5 h-5 items-center justify-center rounded-full" style={{ background: "rgba(200,162,74,0.18)" }}>
                        <Check size={12} className="text-navy" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                {group.note && (
                  <p
                    data-testid={`bring-note-${i}`}
                    className="mt-5 pt-4 border-t border-[var(--maqam-border)] text-xs text-[var(--maqam-muted)] leading-relaxed italic"
                  >
                    {group.note}
                  </p>
                )}
              </>
            );

            return (
              <div key={i} data-testid={`bring-group-${i}`} className="card-elevated rounded-xl p-7">
                {preview ? (
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span>
                        <h3 className="font-serif text-2xl text-navy">{group.category}</h3>
                        <span className="text-xs text-[var(--maqam-muted)]">{group.items.length} essentials</span>
                      </span>
                      <ChevronDown size={20} className="text-[color:var(--maqam-gold)] transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="gold-divider mt-3" />
                    {ItemsList}
                  </details>
                ) : (
                  <>
                    <h3 className="font-serif text-2xl text-navy">{group.category}</h3>
                    <div className="gold-divider mt-3" />
                    {ItemsList}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {preview && (
          <div className="mt-8">
            <Link
              to="/umrah-october-2026"
              data-testid="whattobring-full-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] hover:decoration-2"
            >
              See the full packing checklist →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
